import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Group, Text} from '@mantine/core';
import React from 'react';

import {LLMRole, RoutineExecutionKind, RoutineRunAs} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatMessageSenderContext} from 'llm/chat-library/messages/ChatMessageSenderContext';
import {
  AssistantMessage,
  ClientDisplayChatEvent,
  ClientOptimisticUserMessage,
  DisplayMessageChatEventNode,
} from 'llm/chat-library/messages/MessageTypes';
import {AGENT_AVATAR_COLORS} from 'llm/chat-library/ui/AIStyleConstants';
import {getRunKindLabel} from 'llm/routines/routineExecutionLabels';
import EntityStore from 'stores/EntityStore';
import {HasAvatar} from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {AvatarWithName} from 'ui-library/avatar/AvatarWithName';
import {assertNever} from 'util/Types';

export type MessageCreator = HasAvatar & {id: string};

function currentUserAsMessageCreator(): MessageCreator | null {
  const currentUser = EntityStore.currentUser();
  if (currentUser?.id == null) {
    return null;
  }
  return currentUser as MessageCreator;
}

/**
 * Party shown in "as …" sender metadata when the message has no persisted creator.
 * Derived from the routine execution's run-as mode: session creator for CREATOR, the routine for SERVICE_ACCOUNT.
 */
export function getMessageCreatorFallback(
  senderContext: ChatMessageSenderContext,
  chatSessionCreator: MessageCreator | null | undefined
): MessageCreator | null {
  switch (senderContext.routine.runAs) {
    case RoutineRunAs.CREATOR:
      return chatSessionCreator ?? null;
    case RoutineRunAs.SERVICE_ACCOUNT:
      return senderContext.routine;
    default:
      return assertNever(senderContext.routine.runAs);
  }
}

/** Resolves the party shown on user message sender labels for a display or optimistic event. */
export function resolveUserMessageSenderCreator(
  event: DisplayMessageChatEventNode | ClientOptimisticUserMessage,
  senderContext: ChatMessageSenderContext,
  chatSessionCreator: MessageCreator | null | undefined
): MessageCreator | null {
  switch (event.__typename) {
    case 'ClientOptimisticUserMessage':
      return currentUserAsMessageCreator();
    case 'DisplayMessageChatEventNode':
      return event.creator ?? getMessageCreatorFallback(senderContext, chatSessionCreator);
    default:
      return assertNever(event);
  }
}

/**
 * Resolves the user leg of assistant "as …" metadata.
 * Persisted events prefer the stored creator; streaming events have no creator field and always use run-as fallback.
 */
export function resolveAssistantMessageSenderCreator(
  event: AssistantMessage,
  senderContext: ChatMessageSenderContext,
  chatSessionCreator: MessageCreator | null | undefined
): MessageCreator | null {
  const fallback = getMessageCreatorFallback(senderContext, chatSessionCreator);
  switch (event.__typename) {
    case 'DisplayMessageChatEventNode':
      return event.creator ?? fallback;
    case 'ClientStreamingAssistantMessage':
      // Client-only streaming placeholder; run-as fallback matches persisted messages without creator.
      return fallback;
    default:
      return assertNever(event);
  }
}

/** Counts distinct sender identities for user-role messages (including the routine when it is the author). */
export function countUniqueUserMessageSenders(
  displayEvents: ReadonlyArray<ClientDisplayChatEvent>,
  senderContext: ChatMessageSenderContext,
  chatSessionCreator: MessageCreator | null | undefined
): number {
  const uniqueIds = new Set<string>();
  for (const event of displayEvents) {
    if (event.role !== LLMRole.USER) {
      continue;
    }
    if (
      event.__typename === 'DisplayMessageChatEventNode' ||
      event.__typename === 'ClientOptimisticUserMessage'
    ) {
      const creator = resolveUserMessageSenderCreator(event, senderContext, chatSessionCreator);
      if (creator?.id != null) {
        uniqueIds.add(creator.id);
      }
    }
  }
  return uniqueIds.size;
}

const SENDER_AVATAR_SIZE = AvatarSize.XXSMALL;

/** Decorative bullet and readable metadata after a sender name, sized to match the name. */
function SenderMetadata({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <>
      <Text component="span" c="gray.5" size="md" aria-hidden={true}>
        •
      </Text>
      <Text
        component="span"
        c="gray.6"
        size="md"
        {...getTestProps({component: 'ChatMessageSender', element: 'metadata'})}
      >
        {children}
      </Text>
    </>
  );
}

const usePartyLabelStyles = createStyles((theme) => ({
  userLabel: {
    gap: theme.spacing.xs,
  },
  agentLabel: {
    gap: theme.spacing.sm,
  },
}));

function PartyLabel({party, isAgent = false}: {party: HasAvatar; isAgent?: boolean}): JSX.Element {
  const {classes} = usePartyLabelStyles();
  return (
    <AvatarWithName
      avatarInfo={party}
      size={SENDER_AVATAR_SIZE}
      shape={AvatarShape.CIRCLE}
      showTooltip={false}
      inline={true}
      textAvatarColors={isAgent ? AGENT_AVATAR_COLORS : undefined}
      className={isAgent ? classes.agentLabel : classes.userLabel}
    />
  );
}

/** Sentinel when the sender is the signed-in user; render as primary label "You" or metadata "you". */
const CURRENT_USER_LABEL = Symbol('current-user-label');

function resolveUserLabel(
  creator: MessageCreator | null | undefined
): React.ReactNode | typeof CURRENT_USER_LABEL | null {
  if (creator == null) {
    return null;
  }
  const currentUserId = EntityStore.currentUserId;
  if (currentUserId != null && creator.id === currentUserId) {
    return CURRENT_USER_LABEL;
  }
  return <PartyLabel party={creator} />;
}

function renderRunAsUserLabel(userLabel: React.ReactNode | typeof CURRENT_USER_LABEL): React.ReactNode {
  if (userLabel === CURRENT_USER_LABEL) {
    return 'you';
  }
  return typeof userLabel === 'string' ? userLabel : userLabel;
}

function getUserMessageTriggerLabel(
  senderContext: ChatMessageSenderContext,
  isFirstMessageInThread: boolean
): string | null {
  if (!isFirstMessageInThread) {
    // For now, we show no trigger context on follow-up messages. Per-message origin could
    // distinguish composer replies from the opening run later.
    return null;
  }

  // The first display user message reflects how the chat was opened (one routine execution kind
  // per session), so run-kind metadata applies only there.
  const {executionKind, routine} = senderContext;
  if (executionKind == null) {
    return null;
  }
  if (
    executionKind === RoutineExecutionKind.USER_MESSAGE ||
    executionKind === RoutineExecutionKind.AGENT_MESSAGE
  ) {
    // Direct messaging is the implicit default; sender avatars already convey that context.
    return null;
  }
  return getRunKindLabel(executionKind, routine.triggerType);
}

const SENDER_GROUP_PROPS = {
  spacing: 'sm' as const,
  align: 'center' as const,
  noWrap: true,
  display: 'inline-flex' as const,
};

const useAssistantSenderStyles = createStyles(() => ({
  /** Prevent the agent AvatarWithName from shrinking when "as …" metadata is appended in the same row. */
  agentLabel: {
    flexShrink: 0,
  },
}));

const useSenderStyles = createStyles((theme) => ({
  runAsUserRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    flexWrap: 'nowrap',
  },
}));

export function ChatAssistantMessageSender({
  creator,
  senderContext,
}: {
  creator: MessageCreator | null | undefined;
  senderContext: ChatMessageSenderContext;
}): JSX.Element {
  const {classes: senderClasses} = useSenderStyles();
  const {classes: assistantSenderClasses} = useAssistantSenderStyles();
  const {routine} = senderContext;
  const agentLabel =
    routine.name != null && routine.name !== '' ? (
      <PartyLabel party={{name: routine.name}} isAgent={true} />
    ) : (
      'Agent'
    );
  const userLabel = resolveUserLabel(creator);
  const showRunAsUser = senderContext.routine.runAs !== RoutineRunAs.SERVICE_ACCOUNT && userLabel != null;

  return (
    <Text size="sm" component="div">
      <Group {...SENDER_GROUP_PROPS}>
        <Box component="div" className={assistantSenderClasses.agentLabel}>
          {typeof agentLabel === 'string' ? <span>{agentLabel}</span> : agentLabel}
        </Box>
        {showRunAsUser ? (
          <SenderMetadata>
            <Box component="span" className={senderClasses.runAsUserRow}>
              as {renderRunAsUserLabel(userLabel)}
            </Box>
          </SenderMetadata>
        ) : null}
      </Group>
    </Text>
  );
}

export function ChatUserMessageSender({
  creator,
  senderContext,
  isFirstMessageInThread = false,
  showCurrentUserSender = false,
}: {
  creator: MessageCreator | null | undefined;
  senderContext: ChatMessageSenderContext;
  /**
   * True for the first display user message in the thread. Session run-kind metadata
   * (manual run, scheduled, event-based, etc.) apply only then; follow-ups omit them.
   */
  isFirstMessageInThread?: boolean;
  /** When true, show a sender row for the current user (multi-party threads with 2+ distinct senders). */
  showCurrentUserSender?: boolean;
}): JSX.Element | null {
  const userLabel = resolveUserLabel(creator);
  if (userLabel == null) {
    return null;
  }

  const triggerLabel = getUserMessageTriggerLabel(senderContext, isFirstMessageInThread);
  if (userLabel === CURRENT_USER_LABEL && !showCurrentUserSender && triggerLabel == null) {
    return null;
  }

  return (
    <Text size="sm" component="div">
      <Group {...SENDER_GROUP_PROPS}>
        {typeof userLabel === 'string' ? (
          <span>{userLabel}</span>
        ) : userLabel === CURRENT_USER_LABEL ? (
          <span>You</span>
        ) : (
          userLabel
        )}
        {triggerLabel != null ? <SenderMetadata>{triggerLabel}</SenderMetadata> : null}
      </Group>
    </Text>
  );
}
